import React, { useState, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Typography from "@mui/material/Typography";
import { Grid, Box, Paper } from "@mui/material";
import useAxiosPrivate from "../utils/useAxiosPrivate";
import { useAuth } from "../utils/AuthProvider";
import moment from 'moment';
import i18n from "../translationUtils/i18n";
import muiLocalizationsMap from "../translationUtils/muiLocalizations";

const style = {
  minWidth: '60%',
  overflow: 'scroll',
  overflowX: "hidden",
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  gap: "4%",
  alignItems: "center"
};

function CollapsibleRow(props) {

  const servicePhone = "248-200-3730"

  const convertTime = (timeString) => {
    const time = moment(timeString, 'HH:mm:ss');
    return time.format('h:mm A');
  };

  return (
      <Paper sx={style}>
        <Box>
          {props.row.fileLink ?
            <img
              src={props.row ? props.row.fileLink : null}
              alt={''}
              width={"100%"}
            />
          :
            <Typography fontWeight={"bold"} noWrap>{i18n.t('noimage')}</Typography>
          }
        </Box>
        <Box>
          <Typography noWrap>{i18n.t('unit')}: {props.row ? props.row.productName : null}</Typography>
          <Typography noWrap>{i18n.t('site')}: {props.row ? props.row.siteName : null}</Typography>
          <Typography noWrap>{i18n.t('systemtime')}: {props.row ? props.row.dateOfEvent : null}, {convertTime(props.row ? props.row.systemLocalTime : null)}</Typography>
   
          <Typography noWrap>{i18n.t('accountmanager')}: {props.row ? props.row.accountManager : null}</Typography>
          <Typography noWrap>{i18n.t('accountmanagerphone')}: {props.row ? props.row.accountManagerPhone : null}</Typography>
          <Typography noWrap>{i18n.t('centralstationphone')}: {props.row ? props.row.centralStationPhone : null}</Typography>
          <Typography noWrap>{i18n.t('servicedepartmentphone')}: {servicePhone}</Typography>
        </Box>
      </Paper>
  );
}

export function Notifications() {

  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const convertTime = (timeString) => {
    if (timeString) {
      const time = moment(timeString, 'HH:mm:ss');
      return time.format('h:mm A');
    } else {
      return null
    }
  };

  const loadData = (notifications) => {
    if (notifications && notifications.length > 0) {
      let userRows = [];
      notifications.forEach((notification) => {
        userRows.push(
          {
            id: notification["eventNotificationId"],
            message: "FireRover Activated",
            eventType: notification.isTest ? i18n.t('testalert') + " - " + notification.eventType : notification.eventType,
            productName: notification["productName"],
            siteName: notification["siteName"],
            dateOfEvent: notification["dateOfEvent"],
            systemLocalTime: convertTime(notification["systemLocalTime"]),
            localTime: convertTime(notification["systemLocalTime"]),
            fileLink: notification["fileLink"],
            accountManager: notification["accountManager"],
            accountManagerPhone: notification["accountManagerPhone"],
            centralStationPhone: notification["centralStationPhone"]
          }
        )
      });

      setRows(userRows);
      setLoading(false);
    }
  };

  const fetchData = () => {
    const controller = new AbortController();

    const getEventNotifications = async () => {
      try {
        if (cookies.user) {
          const response = await axiosPrivate.get('/api/User/eventNotifications/' + cookies.user, {
            signal: controller.signal
          })
  
          loadData(response.data.slice().reverse());
        }
      } catch (err) {
        console.log(err);
      }
    }

    getEventNotifications();

    return () => {
      controller.abort();
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line 
  }, []);

  const columns =
    [
      {
        accessorKey: 'eventType',
        header: i18n.t('event'),
        enableColumnOrdering: false,
        size: 30,
      },
      {
        accessorKey: 'productName',
        header: i18n.t('unit'),
        size: 30,
      },
      {
        accessorKey: 'siteName',
        header: i18n.t('site'),
        maxSize: 100,
      },
      {
        accessorKey: 'dateOfEvent',
        header: i18n.t('date'),
        size: 40,
      },
      {
        accessorKey: 'systemLocalTime',
        header: i18n.t('systemtime'),
        size: 50,
      },
      {
        accessorKey: 'localTime',
        header: i18n.t('localtime'),
        size: 50,
      },
    ];

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: muiLocalizationsMap,
    enableExpandAll: true,
    enableColumnOrdering: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    paginationDisplayMode: 'pages',
    layoutMode: 'grid',
    initialState: {
      columnPinning: { left: ['mrt-row-expand', 'eventType'] },
      pagination: { pageSize: 100, pageIndex: 0 },
    },
    state: { 
      showSkeletons: loading,
    },
    muiPaginationProps: {
      shape: 'rounded',
      rowsPerPageOptions: [50, 100, 200],
      showFirstButton: true,
      showLastButton: true,
    },
    muiTableContainerProps: { sx: { maxHeight: '70vh' } },
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content-Actions': {
          display: "none"
        },
        '&:hover .Mui-TableHeadCell-Content-Actions': {
          display: "block"
        },
        '& .Mui-TableHeadCell-Content-Labels span': {
          display: "none"
        },
        '&:hover .Mui-TableHeadCell-Content-Labels span': {
          display: "block"
        },
      },
    },
    muiDetailPanelProps: {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }
    },
    //custom expand button rotation
    muiExpandButtonProps: ({ row }) => ({
      sx: {
        transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
        transition: 'transform 0.2s',
      },
    }),

    //conditionally render detail panel
    renderDetailPanel: ({ row }) =>
      <CollapsibleRow key={row.original.id} row={row.original} />
  });

  return (
    <>
      <Grid
        container
        justifyContent="center" // Center vertically
        alignItems="center" // Center horizontally
        style={{ height: "2%", paddingTop: "1%", marginBottom: "1%" }} // Center vertically
      >
        <Typography
          variant="h6"
          alignItems="center"
          fontFamily="monospace">
          {i18n.t('notifications')}
        </Typography>
      </Grid>
      <MaterialReactTable table={table} />
    </>
  );
}